'use client';

import * as React from 'react';
import { AlertTriangle } from 'lucide-react';

import { Button, Row, Stack, Text } from '../../atoms';
import {
	BodySkeleton,
	CardGrid,
	CellTitleSub,
	DashGrid,
	DataTable,
	type DataTableColumn,
	EmptyCard,
	ExplodeButton,
	Footnote,
	InfoDot,
	InfoStrip,
	KpiBig,
	KpiSkeleton,
	ListSkeleton,
	PanelCard,
	PanelCardSkeleton,
	SegmentedTabs,
	Tag,
} from '../../molecules';
import { PageTitle } from '../../templates';

// ─── Impianti ────────────────────────────────────────────────────────────────

export type AnomalySeverita = 'crit' | 'warn' | 'info';

export interface PlantAnomalyRowItem {
	id: string;
	plantId: string;
	plantNome: string;
	inverterCodice: string | null;
	faultCode: string;
	categoria: string;
	severita: AnomalySeverita;
	titolo: string;
	descrizione: string;
	soluzione: string;
	dettaglio: string | null;
	occurredOn: string;
	rilevatoAt: string | null;
	risoltoAt: string | null;
}

export interface PlantAnomalyCountsItem {
	crit: number;
	warn: number;
	info: number;
	risolteOggi: number;
}

export interface AnomaliesPlantOption {
	id: string;
	nome: string;
}

// ─── Consumi ─────────────────────────────────────────────────────────────────

export type ConsumptionAnomalyKindItem = 'picco' | 'zero_prolungato' | 'crollo';
export type AnomalyStatusFilterItem = 'open' | 'resolved' | 'all';

export interface ConsumptionAnomalyRowItem {
	id: string;
	meterId: string;
	codice: string | null;
	podId: string | null;
	kind: ConsumptionAnomalyKindItem;
	severity: 'critical' | 'warning';
	occurredOn: string;
	lastDay: string;
	days: number;
	value: number | null;
	baseline: number | null;
	detectedAt: string | null;
	resolvedAt: string | null;
	resolution: 'auto' | 'manual' | null;
}

/**
 * Vista attiva della pagina Anomalie quando più configurazioni sono
 * selezionate: `multi` è la griglia «Panoramica» (una card per configurazione),
 * `single` è il pannello completo di una configurazione sola. Stesso schema di
 * Dashboard, Monitoraggio, Previsione e Incentivi.
 */
/** Anomalia anagrafica di POD o impianti (INB-349): si risolve correggendo l'anagrafica. */
export interface RegistryAnomalyRowItem {
	kind: string;
	livello: 'errore' | 'avviso';
	/** Il POD o l'impianto interessato. */
	oggetto: string;
	messaggio: string;
}

export type AnomaliesViewMode = 'multi' | 'single';

/** Voce del picker di configurazione, in modalità singola. */
export interface AnomaliesConfigPickerItem {
	id: string;
	cerNome: string;
	configNome: string;
}

export interface AnomaliesViewProps {
	// ── Impianti
	plantAnomalies?: PlantAnomalyRowItem[];
	plantCounts?: PlantAnomalyCountsItem;
	plantOptions?: AnomaliesPlantOption[];
	/** Filtro impianto attivo; `null` = tutti. */
	selectedPlantId?: string | null;
	onSelectPlant?: (id: string | null) => void;
	/** Quante tipologie di guasto il motore riconosce (dal catalogo, non scritto a mano). */
	faultTypesCount?: number;
	onResolvePlantAnomaly?: (id: string, resolved: boolean) => void;
	plantSavingId?: string | null;
	plantLoading?: boolean;
	plantError?: string | null;
	// ── Consumi
	consumptionAnomalies?: ConsumptionAnomalyRowItem[];
	consumptionStatus?: AnomalyStatusFilterItem;
	onConsumptionStatusChange?: (s: AnomalyStatusFilterItem) => void;
	onResolveConsumptionAnomaly?: (id: string, resolved: boolean) => void;
	consumptionSavingId?: string | null;
	consumptionLoading?: boolean;
	consumptionError?: string | null;
	onExplodeMetric?: (target: { metricId: string; kpiTotal?: number | null }) => void;
	onExplodePodDay?: (target: { podId: string; day: string }) => void;
	// ── Anagrafica (INB-349)
	registryAnomalies?: RegistryAnomalyRowItem[];
	registryLoading?: boolean;
	registryError?: string | null;
	/** Scheda aperta al montaggio. Default: `impianti`. */
	initialTab?: 'impianti' | 'consumi' | 'anagrafica';
	// ── Panoramica configurazioni
	/**
	 * Vista attiva. Assente (o `configs` con un solo elemento): niente toggle,
	 * si mostrano solo le schede Impianti/Consumi — il comportamento di prima
	 * della «Panoramica».
	 */
	view?: AnomaliesViewMode;
	onViewChange?: (view: AnomaliesViewMode) => void;
	/** Configurazioni disponibili per il picker di modalità singola. */
	configs?: AnomaliesConfigPickerItem[];
	/** Configurazione scelta nel picker, in modalità singola. */
	selectedConfigId?: string | null;
	onSelectConfig?: (id: string) => void;
	/**
	 * Contenuto della griglia «Panoramica»: una {@link AnomaliesConfigCard} per
	 * configurazione, fornita dal wiring — ognuna fa il proprio fetch, la vista
	 * non sa come si ottiene.
	 */
	overviewGridSlot?: React.ReactNode;
}

const KIND_LABEL: Record<ConsumptionAnomalyKindItem, string> = {
	picco: 'Picco di consumo',
	zero_prolungato: 'Consumo a zero',
	crollo: 'Crollo dei consumi',
};

const KIND_HINT: Record<ConsumptionAnomalyKindItem, string> = {
	picco: 'Consumo molto sopra la norma del misuratore',
	zero_prolungato: 'Il contatore trasmette, ma non registra prelievo',
	crollo: 'Consumo molto sotto la norma per più giorni',
};

const STATUS_OPTIONS: { value: AnomalyStatusFilterItem; label: string }[] = [
	{ value: 'all', label: 'Tutte' },
	{ value: 'open', label: 'Aperte' },
	{ value: 'resolved', label: 'Risolte' },
];

/**
 * Le tre gravità in maiuscolo, come sulla riga del mock v11 e come sulle card
 * d'impianto del monitoraggio: è la stessa scala, e leggerla scritta in due modi
 * diversi nelle due pagine costringe a ricostruire ogni volta che «Allerta» e
 * «ALLERTA» sono la stessa cosa.
 */
const SEVERITA_LABEL: Record<AnomalySeverita, string> = {
	crit: 'CRITICO',
	warn: 'ALLERTA',
	info: 'INFO',
};

/**
 * Il colore del filetto di gravità della riga, del numero sulla card-contatore
 * e dell'etichetta CRITICO/ALLERTA/INFO — un solo dizionario, come nel mock
 * (`stColor()`, riusata identica per tutti e tre invece di tre tavolozze
 * separate).
 *
 * Il mock v11 li scrive inline perché qui il colore è il dato: la regola
 * «colore uniforme dei valori» in `legacy-utilities.css` riporta all'accento
 * secondario i numeri di tutte le altre card, ma quella riga di quattro conta
 * *gravità*, non una serie di grandezze — un 3 rosso e un 3 verde non vogliono
 * dire la stessa cosa. Per questo i contatori non passano da `.kpi-big`.
 *
 * `crit` è fisso (`#ef4444`), non `var(--color-danger)`: `stColor()` nel mock
 * scrive `'#ef4444'` come letterale per i critici (mentre warning/info restano
 * `var(--warn)`/`var(--accent2)`, temi-dipendenti) — `--color-danger` invece
 * scurisce in tema chiaro (`#dc2626`), disallineandosi dal mock proprio dove
 * la gravità è più alta. Stesso bug corretto in `monitoring-view.tsx`
 * (`STATO_DOT`), stessa funzione mock dietro entrambe le pagine.
 */
const SEVERITA_COLOR: Record<AnomalySeverita, string> = {
	crit: '#ef4444',
	warn: 'var(--color-warn)',
	info: 'var(--color-accent-purple)',
};

const SEVERITA_RANK: Record<AnomalySeverita, number> = { crit: 0, warn: 1, info: 2 };

// ─── Panoramica configurazioni ───────────────────────────────────────────────

const ANOMALIES_VIEW_TABS: { value: AnomaliesViewMode; label: string }[] = [
	{ value: 'multi', label: 'Panoramica' },
	{ value: 'single', label: 'Configurazione singola' },
];

/** Dati di una card della «Panoramica» — il conteggio anomalie aperte di una configurazione sola. */
export interface AnomaliesConfigCardData {
	id: string;
	cerNome: string;
	configNome: string;
	comune?: string | null;
	areaCode?: string | null;
	/** Guasti d'impianto aperti, per gravità. */
	crit: number;
	warn: number;
	/** Irregolarità di consumo ancora aperte. */
	consumiAperte: number;
	/** Anomalie chiuse oggi. */
	risolteOggi: number;
	/** Anomalie anagrafiche di POD e impianti (INB-349). Assente = non ancora note. */
	anagraficaErrori?: number;
	anagraficaAvvisi?: number;
}

export interface AnomaliesConfigCardProps {
	data: AnomaliesConfigCardData;
	onClick?: () => void;
}

function ConfigMetricCell({
	label,
	value,
	color,
}: {
	label: string;
	value: string;
	color: string;
}) {
	return (
		<div className="rounded-[7px] border border-border bg-background px-[9px] py-[7px]">
			<div className="mb-0.5 text-[9px] font-extrabold uppercase tracking-wide text-text-muted">
				{label}
			</div>
			<div className="font-mono text-sm font-extrabold" style={{ color }}>
				{value}
			</div>
		</div>
	);
}

/** Card compatta di una configurazione nella «Panoramica» delle Anomalie: le aperte per gravità, cliccabile per aprirne il pannello completo. */
export function AnomaliesConfigCard({ data, onClick }: AnomaliesConfigCardProps) {
	const badge =
		data.crit > 0
			? { color: SEVERITA_COLOR.crit, label: 'CRITICO' }
			: data.warn > 0 || (data.anagraficaErrori ?? 0) > 0
				? { color: SEVERITA_COLOR.warn, label: 'ALLERTA' }
				: { color: 'var(--color-accent-green)', label: 'OK' };
	const anagrafica = (data.anagraficaErrori ?? 0) + (data.anagraficaAvvisi ?? 0);
	return (
		<button
			type="button"
			onClick={onClick}
			className="w-full cursor-pointer rounded-[11px] border border-border bg-card px-3.5 py-[13px] text-left transition-[border-color,transform] duration-150 hover:-translate-y-0.5 hover:border-accent-purple"
		>
			<div className="mb-3 flex items-start justify-between gap-2">
				<div className="min-w-0">
					<div className="mb-1 truncate text-[9.5px] font-extrabold uppercase tracking-wide text-accent">
						{data.cerNome}
					</div>
					<div className="truncate text-sm font-extrabold leading-tight">{data.configNome}</div>
					<div className="mt-[3px] truncate text-[11px] text-text-muted">
						{data.comune}
						{data.areaCode && (
							<>
								{' · '}
								<span className="font-mono text-accent">{data.areaCode}</span>
							</>
						)}
					</div>
				</div>
				<span
					className="flex-none font-mono text-[9px] font-extrabold"
					style={{ color: badge.color }}
				>
					{badge.label}
				</span>
			</div>
			<div className="mb-3 grid grid-cols-2 gap-2">
				<ConfigMetricCell
					label="Guasti critici"
					value={String(data.crit)}
					color={SEVERITA_COLOR.crit}
				/>
				<ConfigMetricCell label="Allerte" value={String(data.warn)} color={SEVERITA_COLOR.warn} />
				<ConfigMetricCell
					label="Anomalie consumi"
					value={String(data.consumiAperte)}
					color={SEVERITA_COLOR.info}
				/>
				<ConfigMetricCell
					label="Risolte oggi"
					value={String(data.risolteOggi)}
					color="var(--color-accent-green)"
				/>
			</div>
			{anagrafica > 0 && (
				<Text as="div" variant="caption" tone="muted" className="mb-2">
					Anagrafica: {data.anagraficaErrori ?? 0} errori · {data.anagraficaAvvisi ?? 0} avvisi
				</Text>
			)}
			<Text as="span" variant="caption" weight="bold" tone="accent">
				clic → anomalie
			</Text>
		</button>
	);
}

export interface AnomaliesConfigCardSkeletonProps {
	/** Nome della CER — l'unico dato già noto (arriva dallo scope-tree, non dal fetch di questa card): resta nitido, il resto sfoca. */
	cerNome: string;
}

/** Guscio di {@link AnomaliesConfigCard} in caricamento — stessa cornice/padding della card reale. */
export function AnomaliesConfigCardSkeleton({ cerNome }: AnomaliesConfigCardSkeletonProps) {
	return (
		<div
			className="w-full rounded-[11px] border border-border bg-card px-3.5 py-[13px] text-left"
			// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- guscio di card generico (`div`), stesso pattern di `PanelCardSkeleton`.
			role="status"
			aria-label={`Caricamento configurazione - ${cerNome}`}
		>
			<div className="mb-3 flex items-start justify-between gap-2">
				<div className="min-w-0 flex-1">
					<div className="mb-1 truncate text-[9.5px] font-extrabold uppercase tracking-wide text-accent">
						{cerNome}
					</div>
					<BodySkeleton className="mb-1.5 h-4 w-32" />
					<BodySkeleton className="h-3 w-40" />
				</div>
				<BodySkeleton className="h-4 w-12 flex-none rounded-full" />
			</div>
			<div className="mb-3 grid grid-cols-2 gap-2">
				{Array.from({ length: 4 }).map((_, i) => (
					<div
						key={i}
						className="rounded-[7px] border border-border bg-background px-[9px] py-[7px]"
					>
						<BodySkeleton className="mb-1 h-2.5 w-20" />
						<BodySkeleton className="h-4 w-16" />
					</div>
				))}
			</div>
			<BodySkeleton className="h-3 w-28" />
		</div>
	);
}

/** `2026-07-28` → `28/07/2026`. */
function fmtDay(day: string | null): string {
	if (!day) return '-';
	const [y, m, d] = day.slice(0, 10).split('-');
	return d && m && y ? `${d}/${m}/${y}` : day;
}

function fmtDateTime(iso: string | null): string {
	if (!iso) return '-';
	const date = new Date(iso);
	return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('it-IT');
}

function fmtKwh(kwh: number | null): string {
	if (kwh === null || !Number.isFinite(kwh)) return '-';
	return `${kwh.toFixed(1).replace('.', ',')} kWh`;
}

function fmtPeriod(a: ConsumptionAnomalyRowItem): string {
	if (a.days <= 1) return fmtDay(a.occurredOn);
	return `${fmtDay(a.occurredOn)} → ${fmtDay(a.lastDay)} · ${a.days} gg`;
}

/**
 * Come si è chiusa. La distinzione conta: "rientrata" significa che il consumo è
 * tornato normale da solo, "presa in carico" che qualcuno è intervenuto — e su
 * un POD che alterna le due cose la differenza è la diagnosi.
 */
function resolutionLabel(a: ConsumptionAnomalyRowItem): string {
	if (!a.resolvedAt) return 'In corso';
	return a.resolution === 'manual' ? 'Presa in carico' : 'Rientrata';
}

/**
 * Anomalie, su due schede: gli impianti che producono e i POD che consumano.
 *
 * Non sono la stessa cosa e non hanno lo stesso destinatario. Un guasto
 * d'impianto (stringa aperta, inverter in overtemperatura) manda un tecnico in
 * campo; un'irregolarità di consumo (picco, contatore a zero) fa aprire un
 * fascicolo al referente della configurazione. Mescolarle in un'unica lista
 * significherebbe far scorrere a entrambi righe che non li riguardano.
 */
export function AnomaliesView({
	plantAnomalies = [],
	plantCounts = { crit: 0, warn: 0, info: 0, risolteOggi: 0 },
	plantOptions = [],
	selectedPlantId = null,
	onSelectPlant,
	faultTypesCount,
	onResolvePlantAnomaly,
	plantSavingId = null,
	plantLoading = false,
	plantError = null,
	consumptionAnomalies = [],
	consumptionStatus = 'all',
	onConsumptionStatusChange,
	onResolveConsumptionAnomaly,
	consumptionSavingId = null,
	consumptionLoading = false,
	consumptionError = null,
	onExplodeMetric,
	onExplodePodDay,
	registryAnomalies = [],
	registryLoading = false,
	registryError = null,
	initialTab = 'impianti',
	view,
	onViewChange,
	configs = [],
	selectedConfigId = null,
	onSelectConfig,
	overviewGridSlot,
}: AnomaliesViewProps) {
	const [tab, setTab] = React.useState<'impianti' | 'consumi' | 'anagrafica'>(initialTab);
	const [infoOpen, setInfoOpen] = React.useState(false);

	const selectedPlantNome = plantOptions.find((p) => p.id === selectedPlantId)?.nome ?? null;

	// Il toggle Panoramica/Configurazione-singola esiste solo con più di una
	// configurazione in ambito: con una sola, la pagina è quella di prima.
	const configToggle =
		view != null && onViewChange != null && configs.length > 1 ? { view, onViewChange } : null;
	const showOverview = configToggle?.view === 'multi';
	const selectedConfig = configs.find((c) => c.id === selectedConfigId) ?? null;
	const pageTitle =
		configToggle?.view === 'single' && selectedConfig ? selectedConfig.configNome : 'Anomalie';

	return (
		<div>
			{/* Solo titolo e «i» su questa riga: nel mock il filtro non è mai
			    affiancato al titolo, sta sulla propria riga sotto il pannello
			    informativo (`#ano-info` poi `Filtra per impianto:` in un div
			    separato) — spostato dentro ai due pannelli, dopo il loro box
			    info, invece che qui. */}
			<Row gap="sm" justify="between" wrap className="mb-2">
				<Row gap="sm">
					<PageTitle>{pageTitle}</PageTitle>
					{!showOverview && tab === 'impianti' && (
						<InfoDot
							open={infoOpen}
							onToggle={() => setInfoOpen((v) => !v)}
							label="Come funziona il rilevamento dei guasti d'impianto"
						/>
					)}
				</Row>
				{configToggle && (
					<Stack gap="sm" align="stretch">
						<SegmentedTabs
							items={ANOMALIES_VIEW_TABS}
							value={configToggle.view}
							onChange={configToggle.onViewChange}
							ariaLabel="Vista delle anomalie"
							className="mb-0"
						/>
						{configToggle.view === 'single' && onSelectConfig && (
							<select
								value={selectedConfigId ?? ''}
								onChange={(e) => onSelectConfig(e.target.value)}
								aria-label="Configurazione"
								className="min-w-60 rounded-[7px] border border-border bg-card px-2.5 py-1.5 text-[13px] text-text-primary outline-none focus:border-accent"
							>
								{configs.map((c) => (
									<option key={c.id} value={c.id}>
										{c.cerNome} · {c.configNome}
									</option>
								))}
							</select>
						)}
					</Stack>
				)}
			</Row>

			{showOverview ? (
				// `@container` come nella Dashboard (`DashPanel`): senza, le container
				// query `@2xl:` di `DashGrid layout="configs"` non scattano mai e le
				// card restano incolonnate.
				<div className="@container">
					<Text as="div" variant="label" tone="muted" className="mb-2.5">
						Configurazioni selezionate · clicca una card per le anomalie
					</Text>
					<DashGrid layout="configs">{overviewGridSlot}</DashGrid>
				</div>
			) : (
				<>
					<Row gap="sm" className="mb-4 border-b border-border">
						<TabButton active={tab === 'impianti'} onClick={() => setTab('impianti')}>
							Impianti
						</TabButton>
						<TabButton active={tab === 'consumi'} onClick={() => setTab('consumi')}>
							Consumi
						</TabButton>
						<TabButton active={tab === 'anagrafica'} onClick={() => setTab('anagrafica')}>
							{registryAnomalies.length > 0
								? `Anagrafica (${registryAnomalies.length})`
								: 'Anagrafica'}
						</TabButton>
					</Row>

					{tab === 'anagrafica' ? (
						<RegistryAnomaliesPanel
							anomalies={registryAnomalies}
							isLoading={registryLoading}
							error={registryError}
						/>
					) : tab === 'impianti' ? (
						<PlantAnomaliesPanel
							anomalies={plantAnomalies}
							counts={plantCounts}
							faultTypesCount={faultTypesCount}
							infoOpen={infoOpen}
							isLoading={plantLoading}
							error={plantError}
							plantNome={selectedPlantNome}
							onResolve={onResolvePlantAnomaly}
							savingId={plantSavingId}
							plantOptions={plantOptions}
							selectedPlantId={selectedPlantId}
							onSelectPlant={onSelectPlant}
						/>
					) : (
						<ConsumptionAnomaliesPanel
							anomalies={consumptionAnomalies}
							status={consumptionStatus}
							onStatusChange={onConsumptionStatusChange}
							isLoading={consumptionLoading}
							error={consumptionError}
							onResolve={onResolveConsumptionAnomaly}
							savingId={consumptionSavingId}
							onExplodeMetric={onExplodeMetric}
							onExplodePodDay={onExplodePodDay}
						/>
					)}
				</>
			)}
		</div>
	);
}
AnomaliesView.displayName = 'AnomaliesView';

/**
 * Anomalie anagrafiche di POD e impianti (INB-349): la pagina diceva «OK / 0»
 * con un POD malformato, lo stesso POD su due soci o un impianto fuori dai
 * requisiti delle RO. Non hanno un «presa in carico»: spariscono quando si
 * corregge l'anagrafica.
 */
function RegistryAnomaliesPanel({
	anomalies,
	isLoading,
	error,
}: {
	anomalies: RegistryAnomalyRowItem[];
	isLoading: boolean;
	error: string | null;
}) {
	if (error)
		return (
			<PanelCard>
				<Text tone="danger">{error}</Text>
			</PanelCard>
		);
	if (isLoading)
		return (
			<PanelCardSkeleton title="Anagrafica POD e impianti">
				<ListSkeleton rows={3} />
			</PanelCardSkeleton>
		);
	if (anomalies.length === 0)
		return (
			<EmptyCard
				icon={<AlertTriangle />}
				message="Anagrafica di POD e impianti in ordine: codici validi e unici, cabina e potenza presenti, impianti nei requisiti RO."
			/>
		);
	return (
		<PanelCard title="Anagrafica POD e impianti">
			<Stack gap="sm">
				{anomalies.map((a, i) => (
					<Row key={`${a.kind}-${a.oggetto}-${i}`} gap="sm" align="start">
						<Tag tone={a.livello === 'errore' ? 'warn' : 'orange'}>
							{a.livello === 'errore' ? 'Errore' : 'Avviso'}
						</Tag>
						<Stack gap="xs">
							<Text as="span" variant="caption" weight="bold" className="font-mono">
								{a.oggetto}
							</Text>
							<Text as="span" variant="caption" tone="muted">
								{a.messaggio}
							</Text>
						</Stack>
					</Row>
				))}
			</Stack>
		</PanelCard>
	);
}

function TabButton({
	active,
	onClick,
	children,
}: {
	active: boolean;
	onClick: () => void;
	children: React.ReactNode;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			aria-pressed={active}
			className={`-mb-px border-b-2 px-3.5 pb-2.5 pt-2 text-[14px] font-bold transition-colors ${
				active
					? 'border-accent-purple text-accent-purple'
					: 'border-transparent text-text-muted hover:text-text-primary'
			}`}
		>
			{children}
		</button>
	);
}

/** Skeleton di una riga anomalia impianto (guscio `rounded-xl border p-4`, non una `PanelCard`). */
function AnomalyRowSkeleton() {
	return (
		<div
			className="rounded-xl border border-border bg-card p-4"
			// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- guscio generico (`div`), stesso pattern di PanelCardSkeleton/DistributionBar.
			role="status"
			aria-label="Caricamento…"
		>
			<Row className="items-start justify-between gap-3">
				<span className="block w-2/3 space-y-1.5">
					<BodySkeleton className="h-3.5 w-3/4" />
					<BodySkeleton className="h-3 w-1/2" />
				</span>
				<BodySkeleton className="h-5 w-16 shrink-0 rounded-full" />
			</Row>
			<BodySkeleton className="mt-3 h-3 w-full" />
		</div>
	);
}

function PlantAnomaliesPanel({
	anomalies,
	counts,
	faultTypesCount,
	infoOpen,
	isLoading,
	error,
	plantNome,
	onResolve,
	savingId,
	plantOptions,
	selectedPlantId,
	onSelectPlant,
}: {
	anomalies: PlantAnomalyRowItem[];
	counts: PlantAnomalyCountsItem;
	faultTypesCount?: number;
	infoOpen: boolean;
	isLoading: boolean;
	error: string | null;
	/** Nome dell'impianto filtrato, `null` se si guardano tutti. */
	plantNome: string | null;
	onResolve?: (id: string, resolved: boolean) => void;
	savingId: string | null;
	plantOptions: AnomaliesPlantOption[];
	selectedPlantId: string | null;
	onSelectPlant?: (id: string | null) => void;
}) {
	const plantFilterId = React.useId();

	// I guasti critici vanno in cima: l'API ordina per giorno, e un critico di
	// ieri finirebbe sotto una manutenzione programmata di stamattina.
	const sorted = React.useMemo(
		() =>
			[...anomalies].sort(
				(a, b) =>
					SEVERITA_RANK[a.severita] - SEVERITA_RANK[b.severita] ||
					b.occurredOn.localeCompare(a.occurredOn),
			),
		[anomalies],
	);

	return (
		<div>
			{infoOpen && (
				<PanelCard spaced>
					<div className="grid grid-cols-1 gap-5 md:grid-cols-2">
						<span>
							<Text as="span" variant="subtitle" weight="bold" className="mb-1.5 block">
								Dati utilizzati
							</Text>
							<Text as="span" variant="caption" tone="muted" className="block">
								Telemetria degli inverter (potenza AC/DC, energia, temperatura), stato dichiarato
								dai device e irraggiamento sul piano moduli dalla cache meteo della località. Il
								confronto è fra la resa reale e quella teorica a parità di sole, non contro una
								media stagionale: un impianto che rende poco in una giornata coperta non è un
								guasto.
							</Text>
						</span>
						<span>
							<Text as="span" variant="subtitle" weight="bold" className="mb-1.5 block">
								Guasti monitorati
							</Text>
							<Text as="span" variant="caption" tone="muted" className="block">
								{faultTypesCount != null ? (
									<>
										<Text as="strong" variant="inherit">
											{faultTypesCount} tipologie
										</Text>{' '}
										classificate dal motore:
									</>
								) : (
									'Le tipologie classificate dal motore coprono:'
								)}{' '}
								stringa aperta, arco DC, overtemperatura inverter, degrado moduli (PID/LID),
								soiling, fault di comunicazione, degrado dell'accumulo, deviazione tracker.
							</Text>
						</span>
					</div>
					<Footnote>
						La scansione gira sul giorno civile chiuso: su una giornata ancora in corso ogni
						impianto risulterebbe in sottoperformance, avendo prodotto metà giornata contro il sole
						di tutta.
					</Footnote>
				</PanelCard>
			)}

			{/* Riga a sé, dopo il box info e prima dei contatori — stessa posizione
			    del mock (`#ano-info` poi `Filtra per impianto:` in un div separato),
			    non più affiancata al titolo. Markup 1:1 col mock (etichetta col
			    `:`, `<select class="sel">`) invece di `LabeledSelect`: `.sel` ha
			    `width:100%` (`legacy-utilities.css`), quindi dentro questo
			    contenitore flex-wrap la select prende l'intera riga e va sempre a
			    capo sotto l'etichetta — non affiancata, a qualunque larghezza. */}
			{plantOptions.length > 0 && (
				<div className="mb-4 flex flex-wrap items-center gap-2.5">
					<label htmlFor={plantFilterId} className="text-[12px] font-bold text-text-muted">
						Filtra per impianto:
					</label>
					<select
						id={plantFilterId}
						className="sel"
						style={{ minWidth: 260 }}
						value={selectedPlantId ?? ''}
						onChange={(e) => onSelectPlant?.(e.target.value || null)}
					>
						<option value="">Tutti gli impianti</option>
						{plantOptions.map((p) => (
							<option key={p.id} value={p.id}>
								{p.nome}
							</option>
						))}
					</select>
				</div>
			)}

			{isLoading ? (
				<CardGrid columns={4}>
					<SeverityCountSkeleton label="Critici" />
					<SeverityCountSkeleton label="Allerta" />
					<SeverityCountSkeleton label="Informativi" />
					<SeverityCountSkeleton label="Risolti oggi" />
				</CardGrid>
			) : (
				<CardGrid columns={4}>
					<SeverityCount
						label="Critici"
						value={counts.crit}
						hint="intervento immediato"
						tone="crit"
					/>
					<SeverityCount
						label="Allerta"
						value={counts.warn}
						hint="pianificare entro 7 giorni"
						tone="warn"
					/>
					<SeverityCount
						label="Informativi"
						value={counts.info}
						hint="monitoraggio attivo"
						tone="info"
					/>
					<SeverityCount
						label="Risolti oggi"
						value={counts.risolteOggi}
						hint="chiusi nella giornata"
						tone="ok"
					/>
				</CardGrid>
			)}

			{error ? (
				<PanelCard>
					<Text tone="danger">{error}</Text>
				</PanelCard>
			) : isLoading ? (
				<Stack gap="sm">
					<AnomalyRowSkeleton />
					<AnomalyRowSkeleton />
					<AnomalyRowSkeleton />
				</Stack>
			) : sorted.length === 0 ? (
				<EmptyCard
					icon={<AlertTriangle />}
					message={
						plantNome
							? `Nessun guasto aperto su ${plantNome}. La scansione gira ogni notte sul giorno appena chiuso.`
							: 'Nessun guasto aperto sugli impianti. La scansione gira ogni notte sul giorno appena chiuso.'
					}
				/>
			) : (
				<Stack gap="sm">
					{sorted.map((a) => (
						<div
							key={a.id}
							className="anrow"
							style={{ borderLeftColor: SEVERITA_COLOR[a.severita] }}
						>
							<Row align="start" justify="between" gap="md">
								<Text as="span" variant="subtitle" weight="bold">
									{a.titolo}
									{a.inverterCodice ? ` · ${a.inverterCodice}` : ''}
								</Text>
								<Stack align="end" gap="xs" className="shrink-0">
									<Text
										as="span"
										variant="inherit"
										className="font-mono text-[9px] font-extrabold"
										style={{ color: SEVERITA_COLOR[a.severita] }}
									>
										{SEVERITA_LABEL[a.severita]}
									</Text>
									{onResolve && (
										<Button
											type="button"
											variant={a.risoltoAt ? 'ghost' : 'accent'}
											size="sm"
											disabled={savingId === a.id}
											onClick={() => onResolve(a.id, !a.risoltoAt)}
										>
											{a.risoltoAt ? 'Riapri' : 'Prendi in carico'}
										</Button>
									)}
								</Stack>
							</Row>
							{/* Impianto e giorno in accento, come nel mock: sono le due chiavi con
							    cui si va a cercare la riga sul campo, non contorno del titolo. */}
							<Text variant="micro" tone="accent" className="mb-1.5 mt-1">
								{a.plantNome} · {fmtDay(a.occurredOn)}
							</Text>
							<Text variant="caption" tone="muted">
								{a.dettaglio ?? a.descrizione}
							</Text>
							{a.soluzione && (
								<Text variant="micro" tone="success" className="mt-1.5">
									<Text as="strong" variant="inherit" weight="bold">
										Azione consigliata:
									</Text>{' '}
									{a.soluzione}
								</Text>
							)}
						</div>
					))}
				</Stack>
			)}

			<Row justify="end">
				<Footnote>
					Prendere in carico un guasto impedisce alla scansione successiva di riaprirlo per lo
					stesso giorno: «preso in carico» vuol dire anche «smetti di segnalarmelo».
				</Footnote>
			</Row>
		</div>
	);
}

/**
 * Una delle quattro card-contatore in testa alla scheda Impianti.
 *
 * Non passa da `KpiBig` di proposito: là il numero è una grandezza e il tema lo
 * riporta all'accento secondario, qui è una gravità e il colore è metà del
 * significato (vedi {@link SEVERITA_COLOR}).
 */
function SeverityCount({
	label,
	value,
	hint,
	tone,
}: {
	label: string;
	value: number;
	hint: string;
	tone: AnomalySeverita | 'ok';
}) {
	return (
		<PanelCard title={label} centered>
			<div
				className="font-mono text-[30px] font-extrabold leading-none"
				style={{ color: tone === 'ok' ? 'var(--color-accent-green)' : SEVERITA_COLOR[tone] }}
			>
				{value}
			</div>
			<Text variant="micro" tone="muted" className="mt-0.5">
				{hint}
			</Text>
		</PanelCard>
	);
}

/** Skeleton di {@link SeverityCount}: stesso titolo reale, numero e didascalia sfocati/pulsanti. */
function SeverityCountSkeleton({ label }: { label: string }) {
	return (
		<PanelCardSkeleton title={label} centered>
			<BodySkeleton className="mx-auto h-9 w-14" />
			<BodySkeleton className="mx-auto mt-1.5 h-2.5 w-24" />
		</PanelCardSkeleton>
	);
}

function ConsumptionAnomaliesPanel({
	anomalies,
	status,
	onStatusChange,
	isLoading,
	error,
	onResolve,
	savingId,
	onExplodeMetric,
	onExplodePodDay,
}: {
	anomalies: ConsumptionAnomalyRowItem[];
	status: AnomalyStatusFilterItem;
	onStatusChange?: (s: AnomalyStatusFilterItem) => void;
	isLoading: boolean;
	error: string | null;
	onResolve?: (id: string, resolved: boolean) => void;
	savingId: string | null;
	onExplodeMetric?: (t: { metricId: string; kpiTotal?: number | null }) => void;
	onExplodePodDay?: (t: { podId: string; day: string }) => void;
}) {
	const statusFilterId = React.useId();

	const counts = React.useMemo(() => {
		const open = anomalies.filter((a) => !a.resolvedAt);
		return {
			open: open.length,
			critical: open.filter((a) => a.severity === 'critical').length,
			resolved: anomalies.length - open.length,
			// Conteggio sui punti di **connessione**: due misuratori dello stesso POD
			// in anomalia sono un POD in anomalia, non due.
			pods: new Set(open.map((a) => a.podId ?? a.meterId)).size,
		};
	}, [anomalies]);

	const columns: DataTableColumn<ConsumptionAnomalyRowItem>[] = [
		{
			key: 'pod',
			header: 'POD',
			// L'apertura del dettaglio sta qui e non sull'intera riga: la riga porta
			// già il bottone «Prendi in carico», e una riga cliccabile che contiene
			// un bottone è un controllo dentro un controllo — axe la segnala
			// (`nested-interactive`) e da tastiera diventa ambigua. Come effetto
			// collaterale l'affordance diventa visibile: prima nulla diceva che le
			// righe si potessero aprire.
			render: (a) =>
				a.podId ? (
					<button
						type="button"
						className="text-left"
						title="Apri produzione e consumo del POD nel giorno dell'anomalia"
						onClick={() =>
							onExplodePodDay?.({ podId: a.podId as string, day: a.occurredOn.slice(0, 10) })
						}
					>
						<CellTitleSub
							title={a.codice ?? `misuratore ${a.meterId.slice(0, 8)}`}
							sub={KIND_HINT[a.kind]}
						/>
					</button>
				) : (
					<CellTitleSub
						title={a.codice ?? `misuratore ${a.meterId.slice(0, 8)}`}
						sub={KIND_HINT[a.kind]}
					/>
				),
		},
		{
			key: 'kind',
			header: 'Anomalia',
			render: (a) => (
				<Tag tone={a.severity === 'critical' ? 'warn' : 'orange'}>{KIND_LABEL[a.kind]}</Tag>
			),
		},
		{ key: 'period', header: 'Periodo', render: (a) => fmtPeriod(a) },
		{
			key: 'value',
			header: 'Rilevato',
			align: 'right',
			variant: 'monoAccent',
			render: (a) => fmtKwh(a.value),
		},
		{
			key: 'baseline',
			header: 'Atteso',
			align: 'right',
			variant: 'muted',
			render: (a) => fmtKwh(a.baseline),
		},
		{
			key: 'detected',
			header: 'Rilevata il',
			variant: 'muted',
			render: (a) => fmtDateTime(a.detectedAt),
		},
		{
			key: 'status',
			header: 'Stato',
			render: (a) => <Tag tone={a.resolvedAt ? 'done' : 'warn'}>{resolutionLabel(a)}</Tag>,
		},
		{
			key: 'action',
			// Intestazione vera e non vuota: una colonna senza testo di testata è
			// una violazione axe (`empty-table-header`), e per chi naviga con lo
			// screen reader la cella d'azione perde il proprio contesto.
			header: 'Azione',
			align: 'right',
			render: (a) => (
				<Button
					type="button"
					variant={a.resolvedAt ? 'ghost' : 'accent'}
					size="sm"
					disabled={savingId === a.id}
					onClick={() => onResolve?.(a.id, !a.resolvedAt)}
				>
					{a.resolvedAt ? 'Riapri' : 'Prendi in carico'}
				</Button>
			),
		},
	];

	return (
		<div>
			{/* Riga a sé sopra i contatori, stesso markup 1:1 del filtro impianto
			    (etichetta + `<select class="sel">` invece di `LabeledSelect`, per
			    la stessa ragione: la select va sempre a capo sotto l'etichetta,
			    non affiancata). Nel mock non c'è un riferimento diretto per questa
			    scheda, ma tenere la stessa posizione/stile fra le due schede è più
			    coerente che lasciarlo dove stava prima (affiancato al titolo). */}
			<div className="mb-4 flex flex-wrap items-center gap-2.5">
				<label htmlFor={statusFilterId} className="text-[12px] font-bold text-text-muted">
					Stato:
				</label>
				<select
					id={statusFilterId}
					className="sel"
					style={{ minWidth: 260 }}
					value={status}
					onChange={(e) => onStatusChange?.(e.target.value as AnomalyStatusFilterItem)}
				>
					{STATUS_OPTIONS.map((o) => (
						<option key={o.value} value={o.value}>
							{o.label}
						</option>
					))}
				</select>
			</div>

			{isLoading ? (
				<CardGrid columns={4}>
					<PanelCardSkeleton title="Anomalie aperte">
						<KpiSkeleton />
					</PanelCardSkeleton>
					<PanelCardSkeleton title="Di cui critiche">
						<KpiSkeleton />
					</PanelCardSkeleton>
					<PanelCardSkeleton title="POD coinvolti">
						<KpiSkeleton />
					</PanelCardSkeleton>
					<PanelCardSkeleton title="Risolte">
						<KpiSkeleton />
					</PanelCardSkeleton>
				</CardGrid>
			) : (
				<CardGrid columns={4}>
					<PanelCard
						title="Anomalie aperte"
						aside={
							<ExplodeButton
								label="Esplodi le anomalie aperte per POD"
								onClick={() =>
									onExplodeMetric?.({ metricId: 'anomalie_aperte', kpiTotal: counts.open })
								}
								disabled={counts.open === 0}
								disabledReason="Nessuna anomalia aperta da scomporre."
							/>
						}
					>
						<KpiBig
							value={counts.open}
							unit="da verificare"
							tone={counts.open > 0 ? 'warn' : 'success'}
						/>
					</PanelCard>
					<PanelCard title="Di cui critiche">
						<KpiBig
							value={counts.critical}
							unit="consumo a zero o picco grave"
							tone={counts.critical > 0 ? 'danger' : 'success'}
						/>
					</PanelCard>
					<PanelCard
						title="POD coinvolti"
						aside={
							<ExplodeButton
								label="Esplodi i POD coinvolti"
								onClick={() =>
									onExplodeMetric?.({ metricId: 'anomalie_pod_coinvolti', kpiTotal: counts.open })
								}
								disabled={counts.pods === 0}
								disabledReason="Nessun POD con anomalie aperte."
							/>
						}
					>
						<KpiBig
							value={counts.pods}
							unit="punti di connessione con anomalie aperte"
							tone="accent"
						/>
					</PanelCard>
					<PanelCard title="Risolte">
						<KpiBig value={counts.resolved} unit="nello storico caricato" tone="muted" />
					</PanelCard>
				</CardGrid>
			)}

			<PanelCard title="Storico anomalie">
				{error ? (
					<Text tone="danger">{error}</Text>
				) : isLoading ? (
					<div
						// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- guscio generico (`div`), stesso pattern di PanelCardSkeleton/DistributionBar.
						role="status"
						aria-label="Caricamento…"
					>
						<ListSkeleton rows={5} avatar={false} />
					</div>
				) : anomalies.length === 0 ? (
					<EmptyCard
						icon={<AlertTriangle />}
						message={
							status === 'open'
								? 'Nessuna anomalia aperta: i consumi dei POD sono in linea con il loro andamento abituale.'
								: 'Nessuna anomalia registrata. Il controllo gira una volta al giorno sui trenta giorni precedenti.'
						}
					/>
				) : (
					<>
						<DataTable columns={columns} rows={anomalies} rowKey={(a) => a.id} />
						<InfoStrip>
							Un&apos;anomalia si chiude da sola quando il consumo torna nella norma; prenderla in
							carico la chiude subito e impedisce che il controllo la riapra.
						</InfoStrip>
					</>
				)}
			</PanelCard>

			<Row justify="end">
				<Footnote>
					Confronto con la mediana dei 30 giorni precedenti dello stesso misuratore. I POD senza
					misure reali non vengono valutati: le previsioni sono escluse dal confronto.
				</Footnote>
			</Row>
		</div>
	);
}
